import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SearchInput from '@/components/feature/SearchInput';
import PriceCard from '@/components/feature/PriceCard';
import NewsListItem from '@/components/feature/NewsListItem';
import { hotKeywords } from '@/mocks/siteData';
import { priceList } from '@/mocks/priceData';
import { newsList } from '@/mocks/newsData';
import type { NewsItem, PriceItem } from '@/types';

type ResultType = 'all' | 'price' | 'news';

const typeTabs: { key: ResultType; label: string; icon: string }[] = [
  { key: 'all', label: '全部', icon: 'ri-apps-2-line' },
  { key: 'price', label: '行情', icon: 'ri-line-chart-line' },
  { key: 'news', label: '资讯', icon: 'ri-article-line' },
];

function scoreText(keyword: string, primary: string, secondary: string): number {
  const kw = keyword.toLowerCase();
  let score = 0;
  if (primary.toLowerCase().includes(kw)) score += 10;
  if (secondary.toLowerCase().includes(kw)) score += 3;
  return score;
}

export default function SearchPage() {
  const [params] = useSearchParams();
  const keyword = (params.get('q') || '').trim();
  const [type, setType] = useState<ResultType>('all');

  const { prices, news } = useMemo(() => {
    if (!keyword) return { prices: [] as PriceItem[], news: [] as NewsItem[] };

    const scoredPrices = (priceList as PriceItem[])
      .map((item) => ({
        item,
        score: scoreText(keyword, `${item.name} ${item.symbol}`, `${item.market} ${item.type === 'spot' ? '现货' : '期货'}`),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.item);

    const scoredNews = (newsList as NewsItem[])
      .map((item) => ({
        item,
        score: scoreText(keyword, `${item.title} ${item.tag}`, `${item.summary} ${item.author} ${item.content.join(' ')}`),
      }))
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.item);

    return { prices: scoredPrices, news: scoredNews };
  }, [keyword]);

  const showPrices = type === 'all' || type === 'price';
  const showNews = type === 'all' || type === 'news';
  const total = (showPrices ? prices.length : 0) + (showNews ? news.length : 0);
  const hasResult = total > 0;

  return (
    <Layout>
      <title>{keyword ? `搜索：${keyword} - 黄金` : '站内搜索 - 黄金'}</title>
      <meta name="description" content="黄金站内搜索，支持按关键词检索黄金行情数据与投资资讯，并可按内容类型筛选，快速定位你关心的黄金信息。" />
      <meta name="keywords" content="黄金搜索,黄金行情查询,黄金资讯检索" />

      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-portal px-4 py-8 md:px-6 md:py-10">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">站内搜索</h1>
          <p className="mt-2 text-sm text-foreground-600">检索黄金行情与投资资讯，快速定位你关心的内容</p>
          <div className="mt-5 max-w-2xl">
            <SearchInput size="lg" defaultValue={keyword} key={keyword} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-portal px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:flex-row md:items-center md:justify-between md:p-5">
          <div className="text-sm text-foreground-600">
            {keyword ? (
              <>
                关键词「<span className="font-semibold text-foreground-950">{keyword}</span>」共找到{' '}
                <span className="font-semibold text-primary-700">{total}</span> 条结果
              </>
            ) : (
              '请输入关键词开始搜索'
            )}
          </div>
          <div className="flex rounded-full border border-background-300 p-1">
            {typeTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setType(tab.key)}
                className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  type === tab.key ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-primary-700'
                }`}
              >
                <i className={tab.icon} aria-hidden="true"></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {!keyword && (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-search-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">输入关键词，开始探索黄金世界</p>
            <div className="mx-auto mt-5 flex max-w-xl flex-wrap items-center justify-center gap-2">
              {hotKeywords.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        )}

        {keyword && !hasResult && (
          <div className="mt-8 rounded-lg border border-dashed border-background-300 bg-background-50 py-14 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-emotion-sad-line" aria-hidden="true"></i>
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">没有找到与「{keyword}」相关的内容</p>
            <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或浏览以下热门内容。</p>
            <div className="mx-auto mt-5 flex max-w-xl flex-wrap items-center justify-center gap-2">
              {hotKeywords.map((word) => (
                <Link
                  key={word}
                  to={`/search?q=${encodeURIComponent(word)}`}
                  className="rounded-full bg-background-100 px-3 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  {word}
                </Link>
              ))}
            </div>
          </div>
        )}

        {keyword && hasResult && (
          <div className="mt-8 space-y-10">
            {showPrices && prices.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-line-chart-line text-primary-600" aria-hidden="true"></i>
                  行情结果
                  <span className="text-sm font-normal text-foreground-400">({prices.length})</span>
                </h2>
                <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {prices.map((item) => (
                    <PriceCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}

            {showNews && news.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-article-line text-primary-600" aria-hidden="true"></i>
                  资讯结果
                  <span className="text-sm font-normal text-foreground-400">({news.length})</span>
                </h2>
                <div className="mt-4 grid gap-4">
                  {news.map((item) => (
                    <NewsListItem key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}